'use client';

/**
 * Typing certificates.
 *
 * Three tiers so there is something to earn before the very end of a six-week
 * course — a single reward at the very last lesson is too far away to motivate a
 * seven-year-old.
 *
 * Printing goes through the browser's own print-to-PDF. No PDF library is
 * bundled: the print stylesheet in styles/kids.css strips the chrome, forces a
 * white background and keeps the sheet on one page, which produces a genuine
 * vector PDF at whatever paper size the user has.
 *
 * Deliberately NOT called "verified" or "certified" anywhere. It records what
 * a child did on this device; it is not an assessed credential, and saying so
 * would be dishonest.
 */
import React from 'react';
import Link from 'next/link';
import { Printer, Lock } from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import { completedCount, totalStars, MAX_STARS } from '../lib/progress';
import { overview } from '../lib/analytics';
import { ALL_LESSONS, LEVELS } from '../data/curriculum';
import Avatar from './Avatar';
import { LoadingBlock, ProgressRail } from './ui';

interface Tier {
  id: string;
  name: string;
  emoji: string;
  color: string;
  /** Lessons that must be complete. */
  required: number;
  blurb: string;
}

/**
 * Lesson count through the end of `levelIndex`. Derived rather than hardcoded
 * so re-balancing a level cannot silently move a certificate out of reach —
 * the tiers are defined by *what has been learned*, not by a magic number.
 */
const lessonsThroughLevel = (levelIndex: number) =>
  LEVELS.filter((l) => l.index <= levelIndex).reduce((sum, l) => sum + l.lessons.length, 0);

const TIERS: Tier[] = [
  {
    id: 'bronze',
    name: 'Home Row Certificate',
    emoji: '🥉',
    color: '--k-tangerine',
    // Through Level 6 — the whole home row plus the first real words.
    required: lessonsThroughLevel(6),
    blurb: 'for learning the complete home row and typing their first real words',
  },
  {
    id: 'gold',
    name: 'Full Keyboard Certificate',
    emoji: '🥇',
    color: '--k-mango',
    // Through Level 10 — every letter, number and symbol.
    required: lessonsThroughLevel(10),
    blurb: 'for mastering every letter, number and symbol on the keyboard',
  },
  {
    id: 'master',
    name: 'Master Typist Certificate',
    emoji: '👑',
    color: '--k-grape',
    required: ALL_LESSONS.length,
    blurb: 'for completing the entire Kids Typing Journey',
  },
];

export default function Certificate() {
  const { hydrated, profile } = useKids();

  if (!hydrated) return <LoadingBlock height={420} />;
  if (!profile) return null;

  const done = completedCount(profile);
  const stats = overview(profile);
  const earned = TIERS.filter((t) => done >= t.required);
  const best = earned[earned.length - 1];
  const nextTier = TIERS.find((t) => done < t.required);

  return (
    <div className="grid gap-6">
      {best ? (
        <>
          <div className="k-no-print flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              You have earned {earned.length} of {TIERS.length} certificates.
            </p>
            <button
              type="button"
              onClick={() => window.print()}
              className="k-btn k-btn-primary k-btn-sm"
            >
              <Printer size={16} aria-hidden="true" /> Print / save as PDF
            </button>
          </div>

          <Sheet
            tier={best}
            name={profile.name}
            title={profile.equipped.title}
            avatar={profile.avatar}
            wpm={stats.bestWpm}
            accuracy={stats.recentAccuracy || stats.averageAccuracy}
            minutes={stats.totalMinutes}
            lessons={done}
            stars={totalStars(profile)}
          />
        </>
      ) : (
        <div className="k-card-lg p-8 text-center">
          <div style={{ fontSize: 52 }} aria-hidden="true">
            🎓
          </div>
          <h2 className="mt-3 text-xl">Your first certificate is on its way</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Finish Level 6 — the whole home row plus your first real words — and the Home Row
            Certificate unlocks.
          </p>
          <div className="max-w-sm mx-auto mt-5">
            <ProgressRail value={done} max={TIERS[0].required} label="Lessons to go" showValue />
          </div>
          <Link href="/kids/journey" className="k-btn k-btn-primary mt-5">
            Keep going
          </Link>
        </div>
      )}

      {/* ── all tiers ──────────────────────────────────────────────────── */}
      <section className="k-no-print">
        <h2 className="k-eyebrow mb-3">All certificates</h2>
        <ul className="grid gap-3 sm:grid-cols-3">
          {TIERS.map((tier) => {
            const unlocked = done >= tier.required;
            return (
              <li
                key={tier.id}
                className={`k-card p-4 text-center ${unlocked ? '' : 'k-locked'}`}
                style={unlocked ? { borderColor: `var(${tier.color})` } : undefined}
              >
                <span style={{ fontSize: 34 }} aria-hidden="true">
                  {unlocked ? tier.emoji : <Lock size={26} />}
                </span>
                <p className="font-extrabold mt-1.5 text-sm">{tier.name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {unlocked ? 'Earned' : `${tier.required - done} more lessons`}
                </p>
              </li>
            );
          })}
        </ul>
        {nextTier && (
          <p className="text-sm text-muted-foreground mt-4">
            Next up: <strong className="text-foreground">{nextTier.name}</strong> at{' '}
            {nextTier.required} lessons completed. You are at {done}.
          </p>
        )}
      </section>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── the sheet ─────── */

function Sheet({
  tier,
  name,
  title,
  avatar,
  wpm,
  accuracy,
  minutes,
  lessons,
  stars,
}: {
  tier: Tier;
  name: string;
  title: string;
  avatar: React.ComponentProps<typeof Avatar>['config'];
  wpm: number;
  accuracy: number;
  minutes: number;
  lessons: number;
  stars: number;
}) {
  return (
    <article
      className="k-print-sheet relative overflow-hidden text-center px-6 sm:px-10 py-10"
      style={{
        background: 'var(--card)',
        border: `6px double var(${tier.color})`,
        borderRadius: 'var(--k-radius-lg)',
      }}
    >
      <p className="k-eyebrow">TypeSwift · Kids Typing Journey</p>

      <div style={{ fontSize: 52 }} className="mt-2" aria-hidden="true">
        {tier.emoji}
      </div>

      <h2
        className="mt-1"
        style={{ fontSize: 'clamp(1.5rem,1.1rem+2vw,2.3rem)', color: `var(${tier.color})` }}
      >
        {tier.name}
      </h2>

      <p className="text-muted-foreground mt-4">This certificate is awarded to</p>

      <div className="flex items-center justify-center gap-3 mt-3">
        <Avatar config={avatar} size={62} />
        <p
          className="font-extrabold"
          style={{ fontSize: 'clamp(1.5rem,1.1rem+2vw,2.4rem)', lineHeight: 1.1 }}
        >
          {name}
          {title && <span className="block text-base text-muted-foreground">{title}</span>}
        </p>
      </div>

      <p className="text-muted-foreground mt-4 max-w-md mx-auto">{tier.blurb}.</p>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 max-w-2xl mx-auto">
        {[
          ['Best speed', `${wpm} wpm`],
          ['Accuracy', `${accuracy}%`],
          ['Lessons', `${lessons}`],
          ['Practice', `${minutes} min`],
        ].map(([label, value]) => (
          <div key={label} className="py-3 px-2 rounded-2xl" style={{ background: 'var(--muted)' }}>
            <dt className="k-eyebrow">{label}</dt>
            <dd className="font-extrabold text-lg tabular-nums mt-0.5">{value}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-6 text-sm font-bold">
        ⭐ {stars} of {MAX_STARS} stars collected
      </p>

      <footer
        className="mt-8 pt-5 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground"
        style={{ borderTop: `2px solid var(${tier.color})` }}
      >
        <span>Awarded {new Date().toLocaleDateString(undefined, { dateStyle: 'long' })}</span>
        <span>typeswift.app/kids</span>
      </footer>

      {/* Stated plainly so nobody mistakes this for an assessed credential. */}
      <p className="text-[0.65rem] text-faint mt-4 max-w-lg mx-auto leading-relaxed">
        A record of practice completed in the TypeSwift Kids Typing Journey on this device. It is a
        celebration of progress, not an invigilated or externally verified assessment.
      </p>
    </article>
  );
}
